import React from 'react';
import { useTranslation } from 'react-i18next';
import { getAboutStrings } from '@/utils/aboutStrings';
import { StoreDownloadSection } from '@/components/StoreDownloadSection';

/**
 * About page — accessible at /about
 * Presents Falak's identity, services, and stance on traditional moon sighting.
 * Fully translated across all 13 supported languages.
 */
const AboutPage = () => {
  const { i18n } = useTranslation();
  const lang = i18n.language || 'en';
  const isRTL = lang === 'ar' || lang === 'ur' || lang === 'fa';
  const t = getAboutStrings(lang);

  return (
    <div
      style={{
        minHeight: '100vh',
        background: 'linear-gradient(180deg, #0B132B 0%, #050914 100%)',
        color: '#E5E7EB',
        padding: '40px 20px 80px',
        fontFamily: isRTL ? 'system-ui, -apple-system, "Segoe UI", Tahoma, sans-serif' : 'system-ui',
      }}
      dir={isRTL ? 'rtl' : 'ltr'}
      data-testid="about-page"
    >
      <div style={{ maxWidth: 760, margin: '0 auto' }}>
        <a
          href="/"
          style={{
            color: '#0EA5E9',
            textDecoration: 'none',
            fontSize: 14,
            display: 'inline-block',
            marginBottom: 32,
          }}
          data-testid="about-back-link"
        >
          {t.backToHome}
        </a>

        {/* Header — Logo + title */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 24 }}>
          <div
            style={{
              width: 56,
              height: 56,
              borderRadius: '50%',
              position: 'relative',
              background: 'radial-gradient(circle at 30% 30%, #E0C48C 0%, #E0C48C88 40%, transparent 70%)',
              boxShadow: '0 0 32px #E0C48C44',
              flexShrink: 0,
            }}
          >
            <div
              style={{
                position: 'absolute',
                inset: 6,
                borderRadius: '50%',
                background: 'radial-gradient(circle at 70% 50%, transparent 50%, #050914 51%)',
                opacity: 0.9,
              }}
            />
          </div>
          <h1
            style={{
              fontSize: 30,
              fontWeight: 700,
              color: '#FFFFFF',
              margin: 0,
              lineHeight: 1.2,
            }}
            data-testid="about-title"
          >
            {t.pageTitle}
          </h1>
        </div>

        {/* Intro paragraph */}
        <p
          style={{
            fontSize: 17,
            lineHeight: 1.75,
            color: '#CBD5E1',
            marginBottom: 40,
            padding: '20px 24px',
            background: 'rgba(255, 255, 255, 0.04)',
            borderRadius: 12,
            borderInlineStart: '3px solid #0EA5E9',
          }}
          data-testid="about-intro"
        >
          {t.intro}
        </p>

        {/* Services grid */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20, marginBottom: 40 }}>
          {t.services.map((service, idx) => (
            <section
              key={idx}
              style={{
                padding: '20px 24px',
                background: 'rgba(255, 255, 255, 0.03)',
                border: '1px solid rgba(255, 255, 255, 0.08)',
                borderRadius: 12,
              }}
              data-testid={`about-service-${idx + 1}`}
            >
              <h2
                style={{
                  fontSize: 18,
                  fontWeight: 600,
                  color: '#FFFFFF',
                  marginBottom: 10,
                  marginTop: 0,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                }}
              >
                <span
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 28,
                    height: 28,
                    borderRadius: '50%',
                    background: 'rgba(14, 165, 233, 0.15)',
                    color: '#0EA5E9',
                    fontSize: 13,
                    fontWeight: 700,
                    flexShrink: 0,
                  }}
                >
                  {idx + 1}
                </span>
                {service.title}
              </h2>
              <p
                style={{
                  fontSize: 15,
                  lineHeight: 1.75,
                  color: '#CBD5E1',
                  margin: 0,
                }}
              >
                {service.body}
              </p>
            </section>
          ))}
        </div>

        {/* Closing */}
        <div
          style={{
            padding: '24px',
            background: 'rgba(224, 196, 140, 0.06)',
            border: '1px solid rgba(224, 196, 140, 0.2)',
            borderRadius: 12,
            textAlign: 'center',
          }}
          data-testid="about-closing"
        >
          <h3
            style={{
              fontSize: 18,
              fontWeight: 600,
              color: '#E0C48C',
              marginTop: 0,
              marginBottom: 12,
            }}
          >
            {t.closingTitle}
          </h3>
          <p
            style={{
              fontSize: 15,
              lineHeight: 1.75,
              color: '#CBD5E1',
              marginBottom: 16,
            }}
          >
            {t.closingBody}
          </p>
          <p
            style={{
              fontSize: 14,
              color: '#94A3B8',
              margin: 0,
              fontStyle: 'italic',
            }}
          >
            {t.signature}
          </p>
        </div>

        <StoreDownloadSection lang={lang} />

        {/* Credits — English only, elegant card, mounted at the bottom of the page */}
        <div
          dir="ltr"
          style={{
            marginTop: 40,
            padding: '32px 28px',
            background: 'linear-gradient(180deg, rgba(224, 196, 140, 0.06) 0%, rgba(224, 196, 140, 0.02) 100%)',
            border: '1px solid rgba(224, 196, 140, 0.28)',
            borderRadius: 14,
            textAlign: 'center',
            fontFamily: 'system-ui, -apple-system, "Segoe UI", Tahoma, sans-serif',
          }}
          data-testid="about-credits"
        >
          {/* Section header */}
          <div
            style={{
              fontSize: 11,
              letterSpacing: 6,
              fontWeight: 600,
              color: '#E0C48C',
              textTransform: 'uppercase',
              marginBottom: 20,
            }}
          >
            Credits
          </div>

          {/* Divider */}
          <div
            style={{
              width: 40,
              height: 1,
              margin: '0 auto 24px',
              background: 'linear-gradient(90deg, transparent, rgba(224, 196, 140, 0.55), transparent)',
            }}
          />

          {/* Founder name */}
          <div
            style={{
              fontSize: 22,
              fontWeight: 700,
              color: '#F1F5F9',
              letterSpacing: 0.5,
              marginBottom: 8,
            }}
            data-testid="credits-name"
          >
            Riadh Al-Taie
          </div>

          {/* Roles */}
          <div
            style={{
              fontSize: 13,
              color: '#E0C48C',
              letterSpacing: 3,
              textTransform: 'uppercase',
              fontWeight: 500,
              marginBottom: 24,
            }}
            data-testid="credits-roles"
          >
            Founder&nbsp;·&nbsp;Concept&nbsp;·&nbsp;Design
          </div>

          {/* Origin line */}
          <div
            style={{
              fontSize: 13,
              color: '#94A3B8',
              fontStyle: 'italic',
              marginBottom: 24,
              lineHeight: 1.6,
            }}
          >
            Falak is an independent astronomical project<br />
            crafted with care from Amman, Jordan
          </div>

          {/* Divider */}
          <div
            style={{
              width: '100%',
              height: 1,
              margin: '0 auto 20px',
              background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.08), transparent)',
            }}
          />

          {/* Contact */}
          <div
            style={{
              fontSize: 12,
              letterSpacing: 3,
              color: '#94A3B8',
              textTransform: 'uppercase',
              marginBottom: 12,
              fontWeight: 500,
            }}
          >
            Contact
          </div>
          <a
            href="mailto:ffalakkk@gmail.com"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 8,
              color: '#E0C48C',
              textDecoration: 'none',
              fontSize: 15,
              fontWeight: 500,
              padding: '8px 16px',
              borderRadius: 999,
              border: '1px solid rgba(224, 196, 140, 0.35)',
              background: 'rgba(224, 196, 140, 0.06)',
              transition: 'all 0.2s ease',
            }}
            data-testid="credits-email"
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'rgba(224, 196, 140, 0.14)';
              e.currentTarget.style.borderColor = 'rgba(224, 196, 140, 0.6)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'rgba(224, 196, 140, 0.06)';
              e.currentTarget.style.borderColor = 'rgba(224, 196, 140, 0.35)';
            }}
          >
            <span style={{ fontSize: 14 }}>✉</span>
            ffalakkk@gmail.com
          </a>

          {/* Copyright */}
          <div
            style={{
              marginTop: 28,
              paddingTop: 20,
              borderTop: '1px solid rgba(255,255,255,0.05)',
              fontSize: 12,
              color: '#64748B',
              letterSpacing: 1,
            }}
          >
            © 2026 Falak — All rights reserved
          </div>
        </div>
      </div>
    </div>
  );
};

export default AboutPage;
